import { forwardRef, MutableRefObject, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridApi, ColumnApi, IGetRowsParams, RowClickedEvent, RowNode } from 'ag-grid-community';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import Table from 'lib/Table/Table';
import { TableHeight, TableRowHeight } from 'lib/Table/Table.types';
import { CreativesFilters, CreativeStatus } from 'components/common/types/Creative.types';
import { isValidIndex } from 'utils/numberUtils';
import { useNavigateTable } from 'components/pages/ContentManagement/Creatives/Content/Creatives/useNavigateTable';
import { useCreativesTableColumnDef } from './useCreativesTableColumnDef';
import { mapSortColumnToRequestData, normalizePageIndex } from './utils';
import { FetchCreativesParams, FetchCreativesResponse } from './Creatives.types';
import './CreativesTable.pcss';

interface CreativesTableRef {
  refreshTable: () => void;
}

interface CreativesTableProps {
  filters: Partial<CreativesFilters>;
  pageSize: number;
  onFetchCreatives: (params: FetchCreativesParams) => Promise<FetchCreativesResponse>;
  onSelectionChanged: (event: { api: GridApi }) => void;
  reApplyBulkSelection: (tableApi: GridApi | null) => void;
}

export const CreativesTable = forwardRef<CreativesTableRef, CreativesTableProps>(
  ({ pageSize, filters, onFetchCreatives, onSelectionChanged, reApplyBulkSelection }, ref) => {
    const navigate = useNavigate();

    const tableApiRef: MutableRefObject<GridApi | null> = useRef(null);
    const columnApiRef = useRef<ColumnApi | null>(null);
    const onFetchCreativesRef = useRef(onFetchCreatives);
    const filtersStringRef = useRef(JSON.stringify(filters));
    const hasContentReviewPermission = Auth.hasPermission(PermissionsEnum.CONTENT_REVIEW);

    const fetchCreativesTable = useCallback(
      async ({ params, pageIndex }: { params: IGetRowsParams; pageIndex: number }) => {
        const [sortingColumn] = params.sortModel;

        try {
          const { content, totalElements } = await onFetchCreativesRef.current({
            page: pageIndex,
            size: pageSize,
            ...(sortingColumn
              ? {
                  sortBy: mapSortColumnToRequestData(sortingColumn.colId),
                  sortDir: sortingColumn.sort.toUpperCase(),
                }
              : {}),
          });
          return { content, totalElements };
        } catch {
          return { content: [], totalElements: 0 };
        }
      },
      [pageSize],
    );

    const { pageIndex, refreshTable, wirePaginationToSearchParams } = useNavigateTable({
      tableApiRef,
      pageSize,
      fetchCreativesTable,
      reApplyBulkSelection,
    });

    useImperativeHandle(ref, () => ({
      refreshTable,
    }));

    const columnDefs = useCreativesTableColumnDef({
      deleted: filters.deleted || false,
      onStatusUpdate: refreshTable,
      onSelectionChanged,
    });

    const getColumnApi = (columnApi: ColumnApi): void => {
      columnApiRef.current = columnApi;
    };

    const getTableApi = (tableApi: GridApi): void => {
      tableApiRef.current = tableApi;
      wirePaginationToSearchParams(tableApi);
      refreshTable();
    };

    const onRowClicked = (params: RowClickedEvent): void => {
      if (!params.data || params.event?.defaultPrevented || !hasContentReviewPermission) return;

      const target = params.event?.target as HTMLElement;
      const cell = target?.closest('.ag-cell');
      if (cell?.querySelector('.ag-selection-checkbox')) return;
      if (cell?.classList.contains('creatives-table-actions')) return;

      params.event?.preventDefault?.();
      navigate(
        `/content-management/previewCreative/${params.data.id}`,
        isValidIndex(pageIndex)
          ? {
              state: { page: normalizePageIndex(pageIndex) },
            }
          : undefined,
      );
    };

    const debouncedOnSelectionChanged = useCallback(
      debounce((event: { api: GridApi }) => {
        onSelectionChanged(event);
      }, 100),
      [onSelectionChanged],
    );

    useEffect(() => {
      return () => {
        debouncedOnSelectionChanged.cancel();
      };
    }, [debouncedOnSelectionChanged]);

    useEffect(() => {
      onFetchCreativesRef.current = onFetchCreatives;
    }, [onFetchCreatives]);

    useEffect(() => {
      const currentFiltersString = JSON.stringify(filters);
      if (currentFiltersString !== filtersStringRef.current) {
        filtersStringRef.current = currentFiltersString;
        if (tableApiRef.current) {
          refreshTable();
        }
      }
    }, [filters]);

    return (
      <Table
        dataTestId="creatives-table"
        id="creatives-table"
        key={pageSize}
        columnDefs={columnDefs}
        getTableApi={getTableApi}
        getColumnApi={getColumnApi}
        onRowClicked={onRowClicked}
        syncPageIndex={pageIndex}
        extraGridOptions={{
          rowModelType: 'infinite',
          rowHeight: TableRowHeight.NORMAL,
          pagination: true,
          paginationPageSize: pageSize,
          cacheBlockSize: pageSize,
          maxBlocksInCache: 1,
          rowSelection: 'multiple',
          suppressRowClickSelection: true,
          suppressRowTransform: true,
          onSelectionChanged: debouncedOnSelectionChanged,
          enableCellTextSelection: true,
          domLayout: 'autoHeight',
          isRowSelectable: (rowNode: RowNode) => rowNode.data?.statusCode !== CreativeStatus.SENT_FOR_APPROVAL,
        }}
        tableHeight={TableHeight.AUTO}
        className={cx('creatives-table', 'ag-theme-alpine', 'relative', {
          'has-content-review-permission': hasContentReviewPermission,
        })}
      />
    );
  },
);
